import { useCallback, useMemo } from 'react'
import { QueryObserverOptions, useQuery, useQueryClient } from 'react-query'
import APIService from 'api/APIService'
import { ErrorMessage } from 'types/utility'

type QueryKey = string[]

// eslint-disable-next-line
type PromiseLikeFn = (...params: any[]) => Promise<any>

const createDataHook = <FetchFnType extends PromiseLikeFn>(
  queryKeyBase: QueryKey,
  fetchFn: FetchFnType
) => {
  return ({
    params,
    opts
  }: {
    params?: Parameters<FetchFnType>[0]
    opts?: QueryObserverOptions<
      Awaited<ReturnType<typeof fetchFn>>,
      ErrorMessage,
      Awaited<ReturnType<typeof fetchFn>>,
      Awaited<ReturnType<typeof fetchFn>>,
      QueryKey
    >
  } = {}) => {
    const queryKey = useMemo(
      () =>
        params
          ? queryKeyBase.map((path) =>
              path[0] === ':' ? params[path.replace(':', '')] : path
            )
          : queryKeyBase,
      [params]
    )

    const queryClient = useQueryClient()
    const invalidate = useCallback(
      () => queryClient.invalidateQueries(queryKey),
      [queryClient, queryKey]
    )
    // @FIXME: Data is not updating properly even though new data is fresh
    const setData = useCallback(
      (newData: Awaited<ReturnType<typeof fetchFn>>) =>
        queryClient.setQueryData(queryKey, newData),
      [queryClient, queryKey]
    )

    const query = useQuery(queryKey, () => fetchFn(params), opts)

    return {
      ...query,
      invalidate,
      setData
    }
  }
}

export const useUserProfile = createDataHook(
  ['users', 'me'],
  APIService.users.getCurrentUser
)
export const useReport = createDataHook(
  ['reports', ':reportId'],
  APIService.reports.getReport
)
export const useTeam = createDataHook(
  ['team', ':teamId'],
  APIService.teams.getTeam
)
export const useTags = createDataHook(
  [
    'team',
    ':teamId',
    'tags',
    ':page_size',
    ':q',
    ':page',
    ':sort_by',
    ':sort_direction',
    'archived',
    ':archived'
  ],
  APIService.tags.getTags
)
export const useTeamCategories = createDataHook(
  [
    'team',
    ':teamId',
    'categories',
    ':page_size',
    ':q',
    ':page',
    ':sort_by',
    ':sort_direction',
    'archived',
    ':archived'
  ],
  APIService.tags.getTeamCategories
)

export const useRoles = createDataHook(['roles'], APIService.misc.getRoles)
export const usePrograms = createDataHook(
  ['team', ':teamId', 'programs', ':page', ':page_size'],
  APIService.programs.getPrograms
)
export const usePaymentSchedules = createDataHook(
  ['payment-schedules', ':kind'],
  APIService.schedules.getPaymentSchedules
)
export const usePaymentSchedule = createDataHook(
  ['payment-schedule', ':id'],
  APIService.schedules.getPaymentSchedule
)
export const useProgram = createDataHook(
  ['team', ':teamId', 'program', ':programId'],
  APIService.programs.getProgram
)

export const useProgramRates = createDataHook(
  ['team', ':teamId', 'program', ':programId', 'rates'],
  APIService.programs.getProgramRates
)

export const useProgramPurposes = createDataHook(
  ['team', ':teamId', 'program', ':programId', 'purposes'],
  APIService.programs.getProgramPurposes
)

export const useProgramHistory = createDataHook(
  ['user-program-history', ':userId'],
  APIService.programs.getProgramHistory
)

export const useMembers = createDataHook(
  [
    'members',
    ':teamId',
    'search',
    ':q',
    ':is_manager',
    ':filters',
    ':filterByTeam'
  ],
  APIService.members.getTeamMembers
)

export const useMember = createDataHook(
  ['members', ':teamId', ':memberId'],
  APIService.members.getMember
)

export const usePayPeriod = createDataHook(
  ['pay-periods', ':id'],
  APIService.payPeriods.getPayPeriod
)

export const usePayPeriods = createDataHook(
  ['pay-periods', ':status', ':pageSize', ':page', ':issues'],
  APIService.payPeriods.getPayPeriods
)

export const usePreviewPaySchedule = createDataHook(
  ['preview-pay-schedule', ':filters'],
  APIService.schedules.getPaymentSchedulePreview
)

export const useRuleIntrospection = createDataHook(
  ['rule-introspection'],
  APIService.rules.getRuleIntrospection
)

export const useUserComplianceSubmission = createDataHook(
  ['submission', 'me', ':annualized_mileage_year', ':use_new_endpoint'], // confirm with backend if no_program_enabled is still being used - if not remove it
  APIService.compliance.getUserSubmission
)

export const useUserComplianceHistory = createDataHook(
  ['compliance-history', 'me'],
  APIService.compliance.getUserComplianceHistory
)

export const useComplianceHistory = createDataHook(
  ['compliance-history', ':user_id'],
  APIService.compliance.getComplianceHistory
)
export const useAccountant = createDataHook(
  ['user', 'accountants'],
  APIService.misc.getAccountantGroup
)

export const useSubscriptionInvoices = createDataHook(
  ['subscription', 'invoices'],
  APIService.subscriptions.getSubscriptionInvoices
)
export const useOnboardingStats = createDataHook(
  ['onboarding', 'stats', ':teamId', ':archived', ':include_invites'],
  APIService.members.getOnboardingStats
)
